import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { toast } from "@/components/ui/use-toast";
import { Shield, Lock, CheckCircle2, ArrowLeft } from "lucide-react";
import { api } from "@/api";

const UserSecuritySettings = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [method, setMethod] = useState(null); // 'EMAIL' | 'TOTP' | null
  const [chooseMethod, setChooseMethod] = useState('EMAIL');
  const [setupMode, setSetupMode] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [secret, setSecret] = useState(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [password, setPassword] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const s = await api.user2faStatus(token);
        setTwoFactorEnabled(!!s.twoFactorEnabled);
        setMethod(s.method || null);
        if (s.method) setChooseMethod(s.method);
      } catch (e) {
        toast({ title: t('user.security.loadErrorTitle'), description: e.message, variant: "destructive" });
      } finally { setLoading(false); }
    })();
  }, [token]);

  const startTotpSetup = async () => {
    try {
      const d = await api.user2faSetup(token);
      setSecret(d.secret);
      setQrCode(d.qrCode);
      setSetupMode(true);
    } catch (e) {
      toast({ title: t('user.security.errorTitle'), description: e.message, variant: "destructive" });
    }
  };

  const enable2FA = async () => {
    try {
      if (chooseMethod === 'EMAIL') {
        await api.user2faEnable(token, 'EMAIL');
        setTwoFactorEnabled(true);
        setMethod('EMAIL');
        toast({ title: t('user.security.enabledEmailTitle') });
        return;
      }
      if (!verificationCode || verificationCode.length !== 6) {
        toast({ title: t('user.security.enterCodeTitle'), description: t('user.security.enterCodeDesc'), variant: 'destructive' });
        return;
      }
      await api.user2faEnable(token, 'TOTP', verificationCode);
      setTwoFactorEnabled(true);
      setMethod('TOTP');
      setSetupMode(false);
      setVerificationCode("");
      toast({ title: t('user.security.enabledTotpTitle') });
    } catch (e) {
      toast({ title: t('user.security.errorTitle'), description: e.message, variant: 'destructive' });
    }
  };

  const disable2FA = async () => {
    if (!password || password.length < 1) {
      toast({ title: t('user.security.passwordRequired'), variant: 'destructive' });
      return;
    }
    try {
      await api.user2faDisable(token, password);
      setTwoFactorEnabled(false);
      setMethod(null);
      setPassword("");
      toast({ title: t('user.security.disabledTitle') });
    } catch (e) {
      toast({ title: t('user.security.errorTitle'), description: e.message, variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  // Employees haben keine 2FA-Optionen
  if (user?.role === 'EMPLOYEE') {
    return (
      <div className="min-h-screen bg-muted p-4 sm:p-6">
        <div className="max-w-2xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle>{t('user.security.notAvailableTitle')}</CardTitle>
            </CardHeader>
            <CardContent>
              {t('user.security.notAvailableDesc')}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted p-4 sm:p-6">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="bg-background rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Shield className="h-5 w-5" />
              {t('user.security.title')}
            </h1>
            <Button variant="outline" size="sm" onClick={() => navigate(-1)} className="flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              {t('common.back')}
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              {t('user.security.twoFaTitle')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <div className="font-medium">{t('user.security.twoFaStatus')}</div>
                <div className="text-sm text-muted-foreground">
                  {twoFactorEnabled ? t('user.security.twoFaEnabled', { method: method || t('user.security.unknown') }) : t('user.security.twoFaDisabled')}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {twoFactorEnabled ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                ) : (
                  <div className="h-5 w-5 rounded-full border-2 border-border" />
                )}
              </div>
            </div>

            {!twoFactorEnabled && !setupMode && (
              <div className="space-y-4">
                <div>
                  <div className="font-medium mb-2">{t('user.security.chooseMethod')}</div>
                  <div className="flex gap-4 text-sm">
                    <label className="flex items-center gap-2">
                      <input type="radio" name="method" value="EMAIL" checked={chooseMethod==='EMAIL'} onChange={()=>setChooseMethod('EMAIL')} />
                      {t('user.security.method.email')}
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="radio" name="method" value="TOTP" checked={chooseMethod==='TOTP'} onChange={()=>setChooseMethod('TOTP')} />
                      {t('user.security.method.totp')}
                    </label>
                  </div>
                </div>
                {chooseMethod === 'EMAIL' ? (
                  <Button onClick={enable2FA}>{t('user.security.enableEmail')}</Button>
                ) : (
                  <Button onClick={startTotpSetup}>{t('user.security.setupTotp')}</Button>
                )}
              </div>
            )}

            {setupMode && (
              <div className="space-y-4">
                <div className="p-4 border rounded-lg bg-blue-50">
                  <h3 className="font-medium mb-2">{t('user.security.step1Title')}</h3>
                  {qrCode && <div className="flex justify-center"><img src={qrCode} alt={t('user.security.qrAlt')} className="border p-2 bg-background rounded" /></div>}
                  {secret && (
                    <div className="mt-4">
                      <p className="text-sm text-muted-foreground mb-2">{t('user.security.manualCode')}</p>
                      <code className="block p-2 bg-background border rounded text-sm font-mono">{secret}</code>
                    </div>
                  )}
                </div>
                <div className="p-4 border rounded-lg">
                  <h3 className="font-medium mb-2">{t('user.security.step2Title')}</h3>
                  <input
                    type="text"
                    placeholder={t('user.security.codePlaceholder')}
                    maxLength={6}
                    value={verificationCode}
                    onChange={(e)=>setVerificationCode(e.target.value.replace(/\D/g, ''))}
                    className="w-full px-3 py-2 border rounded-md mb-4"
                  />
                  <div className="flex gap-2">
                    <Button onClick={enable2FA}>{t('user.security.enable2fa')}</Button>
                    <Button variant="outline" onClick={()=>{ setSetupMode(false); setVerificationCode(''); setQrCode(null); setSecret(null); }}>{t('common.cancel')}</Button>
                  </div>
                </div>
              </div>
            )}

            {twoFactorEnabled && (
              <div className="space-y-4">
                <div className="p-4 border rounded-lg bg-green-50">
                  <p className="text-sm text-green-800">{t('user.security.enabledBanner', { method })}</p>
                </div>
                <div className="p-4 border rounded-lg">
                  <h3 className="font-medium mb-2 text-red-600">{t('user.security.disableTitle')}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{t('user.security.disableDesc')}</p>
                  <input type="password" value={password} onChange={(e)=>setPassword(e.target.value)} className="w-full px-3 py-2 border rounded-md mb-4" placeholder={t('user.security.passwordPlaceholder')} />
                  <Button variant="destructive" onClick={disable2FA}>{t('user.security.disable2fa')}</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default UserSecuritySettings;
